import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { expensesAPI } from '../../services/api';
import { format } from 'date-fns';
import { Plus, Download, FileText, Search, CreditCard, ArrowLeft, Eye, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface Expense {
    _id: string;
    date: string;
    category: string;
    amount: number;
    description: string;
    receiptUrl?: string;
    recordedBy: {
        firstName: string;
        lastName: string;
    };
    siteId: {
        _id: string;
        name: string;
    };
}

const Expenses: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, count: 0 });
    const [dateFilter, setDateFilter] = useState({
        startDate: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd')
    });

    const fetchExpenses = async (filters?: any) => {
        try {
            setLoading(true);
            const params = {
                startDate: filters?.startDate || dateFilter.startDate,
                endDate: filters?.endDate || dateFilter.endDate
            };
            const res = await expensesAPI.getExpenses(params);
            setExpenses(res.data.data);
            setStats({ total: res.data.total, count: res.data.count });
        } catch (error) {
            console.error('Failed to load expenses', error);
            toast.error('Failed to load expenses');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExpenses();
    }, []);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'view' | 'edit'>('add');
    const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        date: format(new Date(), 'yyyy-MM-dd'),
        category: 'Supplies',
        amount: '',
        description: '',
    });
    const [receiptFile, setReceiptFile] = useState<File | null>(null);

    const resetForm = () => {
        setFormData({
            date: format(new Date(), 'yyyy-MM-dd'),
            category: 'Supplies',
            amount: '',
            description: '',
        });
        setReceiptFile(null);
        setSelectedExpense(null);
    };

    const openAddModal = () => {
        resetForm();
        setModalMode('add');
        setShowModal(true);
    };

    const openExpenseModal = (expense: Expense, mode: 'view' | 'edit') => {
        setSelectedExpense(expense);
        setFormData({
            date: format(new Date(expense.date), 'yyyy-MM-dd'),
            category: expense.category,
            amount: expense.amount.toString(),
            description: expense.description,
        });
        setReceiptFile(null);
        setModalMode(mode);
        setShowModal(true);
    };

    const closeModal = () => {
        if (isSubmitting) return;
        setShowModal(false);
        resetForm();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setReceiptFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const data = new FormData();
            data.append('date', formData.date);
            data.append('category', formData.category);
            data.append('amount', formData.amount);
            data.append('description', formData.description);
            if (receiptFile) {
                data.append('receiptUrl', receiptFile);
            }

            if (modalMode === 'edit' && selectedExpense) {
                await expensesAPI.updateExpense(selectedExpense._id, data);
                toast.success('Expense updated successfully');
            } else {
                await expensesAPI.createExpense(data);
                toast.success('Expense added successfully');
            }
            setShowModal(false);
            resetForm();
            fetchExpenses();
        } catch (error: any) {
            console.error('Error creating expense:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to add expense';
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (expense: Expense) => {
        if (!window.confirm(`Delete the ${expense.category} expense of £${expense.amount.toFixed(2)}? This cannot be undone.`)) {
            return;
        }

        try {
            setDeletingId(expense._id);
            await expensesAPI.deleteExpense(expense._id);
            toast.success('Expense deleted successfully');
            await fetchExpenses();
        } catch (error: any) {
            console.error('Error deleting expense:', error);
            toast.error(error.response?.data?.error || 'Failed to delete expense');
        } finally {
            setDeletingId(null);
        }
    };

    const handleDateFilterChange = () => {
        fetchExpenses();
    };

    const setQuickDateRange = (range: string) => {
        const now = new Date();
        let startDate: Date;
        let endDate: Date = new Date();

        switch (range) {
            case 'current_month':
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                break;
            case 'last_month':
                startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                endDate = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            case 'this_year':
                startDate = new Date(now.getFullYear(), 0, 1);
                break;
            case 'last_30_days':
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                break;
            case 'last_7_days':
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            default:
                return;
        }

        const newFilter = {
            startDate: format(startDate, 'yyyy-MM-dd'),
            endDate: format(endDate, 'yyyy-MM-dd')
        };
        setDateFilter(newFilter);
        
        // Auto-apply the filter
        fetchExpenses(newFilter);
    };

    const handleExportCSV = () => {
        if (expenses.length === 0) {
            toast.error('No expenses to export');
            return;
        }

        const headers = ['Date', 'Category', 'Amount', 'Description', 'Recorded By'];
        const rows = expenses.map(e => [
            format(new Date(e.date), 'yyyy-MM-dd'),
            e.category,
            e.amount.toFixed(2),
            `"${e.description.replace(/"/g, '""')}"`, // escape quotes for CSV
            `${e.recordedBy?.firstName} ${e.recordedBy?.lastName}`
        ]);

        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `expenses_export_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                        title="Back to Dashboard"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
                        <p className="text-gray-500 mt-1">Manage and track your site expenses</p>
                    </div>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={handleExportCSV}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm font-medium">
                        <Download className="w-4 h-4" />
                        Export CSV
                    </button>
                    <button
                        onClick={openAddModal}
                        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm font-medium">
                        <Plus className="w-4 h-4" />
                        Add Expense
                    </button>
                </div>
            </div>

            {/* Date Filter */}
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                <div className="flex flex-col gap-4">
                    {/* Quick Date Range Buttons */}
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setQuickDateRange('last_7_days')}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            Last 7 Days
                        </button>
                        <button
                            onClick={() => setQuickDateRange('last_30_days')}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            Last 30 Days
                        </button>
                        <button
                            onClick={() => setQuickDateRange('current_month')}
                            className="px-3 py-1 text-sm bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
                        >
                            Current Month
                        </button>
                        <button
                            onClick={() => setQuickDateRange('last_month')}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            Last Month
                        </button>
                        <button
                            onClick={() => setQuickDateRange('this_year')}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            This Year
                        </button>
                    </div>
                    
                    {/* Custom Date Range */}
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Custom Date Range</label>
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <input
                                        type="date"
                                        value={dateFilter.startDate}
                                        onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
                                        className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-primary-400 focus:outline-none transition-colors"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">From</p>
                                </div>
                                <div className="flex-1">
                                    <input
                                        type="date"
                                        value={dateFilter.endDate}
                                        onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
                                        className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-primary-400 focus:outline-none transition-colors"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">To</p>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={handleDateFilterChange}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium flex items-center gap-2"
                        >
                            <Search className="w-4 h-4" />
                            Apply Filter
                        </button>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500">Total Expenses</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">£{stats.total.toFixed(2)}</p>
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600">
                        <CreditCard className="w-6 h-6" />
                    </div>
                </div>
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500">Expense Records</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{stats.count}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                        <FileText className="w-6 h-6" />
                    </div>
                </div>
            </div>

            {/* Expenses List */}
            {loading ? (
                <div className="flex justify-center p-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recorded By</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {expenses.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                                        No expenses recorded yet.
                                    </td>
                                </tr>
                            ) : (
                                expenses.map(expense => (
                                    <tr key={expense._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {format(new Date(expense.date), 'dd MMM yyyy')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                {expense.category}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={expense.description}>
                                            {expense.description}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                            £{expense.amount.toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {expense.recordedBy?.firstName} {expense.recordedBy?.lastName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {expense.receiptUrl ? (
                                                <a href={`http://localhost:3001${expense.receiptUrl}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-800 flex items-center gap-1 font-medium">
                                                    <FileText className="w-4 h-4" /> View Receipt
                                                </a>
                                            ) : (
                                                <span className="text-gray-400 italic">No receipt</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => openExpenseModal(expense, 'view')}
                                                    className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                                    title="View expense"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => openExpenseModal(expense, 'edit')}
                                                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="Edit expense"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                {(user?.role === 'admin' || user?.role === 'super_admin') && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(expense)}
                                                        disabled={deletingId === expense._id}
                                                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                                        title="Delete expense"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add, view and edit expense modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-900">
                                {modalMode === 'add' ? 'Add New Expense' : modalMode === 'edit' ? 'Edit Expense' : 'Expense Details'}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <Plus className="w-6 h-6 rotate-45" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                    <input
                                        type="date"
                                        required
                                        disabled={modalMode === 'view'}
                                        value={formData.date}
                                        onChange={e => setFormData({ ...formData, date: e.target.value })}
                                        className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 focus:border-primary-400 focus:outline-none transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (£)</label>
                                    <input
                                        type="number"
                                        required
                                        min="0"
                                        step="0.01"
                                        disabled={modalMode === 'view'}
                                        placeholder="0.00"
                                        value={formData.amount}
                                        onChange={e => setFormData({ ...formData, amount: e.target.value })}
                                        className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 focus:border-primary-400 focus:outline-none transition-colors"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                <select
                                    required
                                    disabled={modalMode === 'view'}
                                    value={formData.category}
                                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 focus:border-primary-400 focus:outline-none transition-colors bg-white"
                                >
                                    <option value="Supplies">Supplies</option>
                                    <option value="Utilities">Utilities</option>
                                    <option value="Maintenance">Maintenance</option>
                                    <option value="Software">Software</option>
                                    <option value="Payroll">Payroll</option>
                                    <option value="Rent">Rent</option>
                                    <option value="Marketing">Marketing</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <input
                                    type="text"
                                    required
                                    disabled={modalMode === 'view'}
                                    placeholder="What was this expense for?"
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 focus:border-primary-400 focus:outline-none transition-colors"
                                />
                            </div>

                            {modalMode !== 'view' && <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Image (Optional)</label>
                                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-primary-400 transition-colors bg-gray-50 group cursor-pointer relative">
                                    <div className="space-y-1 text-center">
                                        <FileText className="mx-auto h-8 w-8 text-gray-400 group-hover:text-primary-500 transition-colors" />
                                        <div className="flex text-sm text-gray-600 justify-center">
                                            <span className="relative rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none">
                                                {receiptFile ? receiptFile.name : 'Upload a file'}
                                                <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} accept="image/*,.pdf" />
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-500">PNG, JPG, PDF up to 5MB</p>
                                    </div>
                                    <input
                                        type="file"
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        onChange={handleFileChange}
                                        accept="image/*,.pdf"
                                    />
                                </div>
                            </div>}

                            {modalMode === 'view' && selectedExpense?.receiptUrl && (
                                <a
                                    href={`http://localhost:3001${selectedExpense.receiptUrl}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-800 font-medium"
                                >
                                    <FileText className="w-4 h-4" /> View receipt
                                </a>
                            )}

                            <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                >
                                    {modalMode === 'view' ? 'Close' : 'Cancel'}
                                </button>
                                {modalMode === 'view' && (
                                    <button
                                        type="button"
                                        onClick={() => setModalMode('edit')}
                                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium flex items-center gap-2"
                                    >
                                        <Pencil className="w-4 h-4" /> Edit Expense
                                    </button>
                                )}
                                {modalMode !== 'view' && <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        modalMode === 'edit' ? 'Update Expense' : 'Save Expense'
                                    )}
                                </button>}
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Expenses;
